import React, { useState, useEffect, useRef } from 'react';
import Settings from './Settings';
import Admin from './Admin';
import { authFetch } from '../authConfig';

const API_BASE = process.env.REACT_APP_API_URL || '';

const TABS = [
    { id: 'users', label: 'Users' },
    { id: 'promotions', label: 'Promotions' },
    { id: 'items', label: 'Items' },
    { id: 'logs', label: 'Logs' },
    { id: 'feedback', label: 'Feedback' }
];

function AdminPanel({ currentUser, isSuperAdmin, onClose }) {
    const [activeTab, setActiveTab] = useState('users');
    const visibleTabs = isSuperAdmin ? TABS : TABS.filter(tab => tab.id !== 'logs');

    return (
        <div className="admin-panel-overlay">
            <div className="admin-panel-container">
                <div className="admin-panel-header">
                    <h2>Admin</h2>
                    <div className="admin-panel-tabs">
                        {visibleTabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>

                <div className="admin-panel-body">
                    {activeTab === 'users' && (
                        <Settings currentUser={currentUser} embedded />
                    )}
                    {activeTab === 'promotions' && (
                        <Admin embedded />
                    )}
                    {activeTab === 'items' && (
                        <ItemsTab />
                    )}
                    {activeTab === 'logs' && (
                        <LogsTab />
                    )}
                    {activeTab === 'feedback' && (
                        <FeedbackTab />
                    )}
                </div>
            </div>
        </div>
    );
}

const EMPTY_ITEM_FORM = {
    category: '',
    subcategory: '',
    series: '',
    itemDescription: '',
    groupedName: '',
    pmSku: ''
};

function ItemsTab() {
    const [formData, setFormData] = useState(EMPTY_ITEM_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [showImportModal, setShowImportModal] = useState(false);
    const [importMode, setImportMode] = useState('merge');
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    function updateField(field, value) {
        setFormData(prev => ({ ...prev, [field]: value }));
    }

    async function handleExport() {
        try {
            const res = await authFetch(`${API_BASE}/api/export/items`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
                alert('Export failed: ' + (err.error || res.statusText));
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Product Attributes Export.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert('Export failed: ' + err.message);
        }
    }

    function handleFileSelect(e) {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            await performImport(event.target.result);
        };
        reader.readAsText(file);
    }

    async function performImport(csvData) {
        setImporting(true);
        try {
            const res = await authFetch(`${API_BASE}/api/import/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ csvData, mode: importMode })
            });

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                const text = await res.text();
                throw new Error(text || `Server error (${res.status})`);
            }

            const result = await res.json();

            if (res.ok) {
                alert(`Import successful!\n${result.message}`);
                setShowImportModal(false);
            } else {
                alert('Import error: ' + result.error);
            }
        } catch (err) {
            alert('Import failed: ' + err.message);
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await authFetch(`${API_BASE}/api/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const result = await res.json();
            if (!res.ok) {
                throw new Error(result.error || 'Failed to add item');
            }

            const linkedNote = result.linkedPromotionItems > 0
                ? ` It was also linked to ${result.linkedPromotionItems} existing promotion item(s) that were waiting to be matched.`
                : '';
            setSuccessMessage(`"${result.groupedName}" was added to the catalog and will now show up in the item filter.${linkedNote}`);
            setFormData(EMPTY_ITEM_FORM);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="items-tab">
            <p className="items-tab-intro">
                Register a new item in the product catalog so it appears in the Items filter
                right away, without needing a full CSV upload. If a promotion already references
                this item by name, it will be linked automatically. Use Export/Import CSV below
                to bulk update the catalog or compare it against another list.
            </p>

            <div className="items-toolbar">
                <button className="secondary" onClick={handleExport}>Export CSV</button>
                <button className="secondary" onClick={() => setShowImportModal(true)}>Import CSV</button>
            </div>

            {error && <div className="error">{error}</div>}
            {successMessage && <div className="success">{successMessage}</div>}

            <form onSubmit={handleSubmit} className="items-form">
                <div className="form-row">
                    <div className="form-group">
                        <label>Category *</label>
                        <input
                            type="text"
                            value={formData.category}
                            onChange={(e) => updateField('category', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Subcategory *</label>
                        <input
                            type="text"
                            value={formData.subcategory}
                            onChange={(e) => updateField('subcategory', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Series *</label>
                        <input
                            type="text"
                            value={formData.series}
                            onChange={(e) => updateField('series', e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>Item Description *</label>
                    <input
                        type="text"
                        value={formData.itemDescription}
                        onChange={(e) => updateField('itemDescription', e.target.value)}
                        placeholder="e.g. File Cabinet - White"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Grouped Name (used for filtering) *</label>
                    <input
                        type="text"
                        value={formData.groupedName}
                        onChange={(e) => updateField('groupedName', e.target.value)}
                        placeholder="e.g. File Cabinet"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>SKU</label>
                    <input
                        type="text"
                        value={formData.pmSku}
                        onChange={(e) => updateField('pmSku', e.target.value)}
                        placeholder="Optional for now"
                    />
                </div>

                <div className="form-actions">
                    <button type="submit" className="primary" disabled={submitting}>
                        {submitting ? 'Adding...' : 'Add Item'}
                    </button>
                </div>
            </form>

            {showImportModal && (
                <div className="items-import-modal-overlay">
                    <div className="items-import-modal">
                        <h3>Import Products from CSV</h3>

                        <div className="items-import-options">
                            <label>
                                <input
                                    type="radio"
                                    name="itemsImportMode"
                                    value="merge"
                                    checked={importMode === 'merge'}
                                    onChange={(e) => setImportMode(e.target.value)}
                                />
                                <strong>Merge</strong> - Add new products, update existing ones (matched by PM SKU)
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="itemsImportMode"
                                    value="replace"
                                    checked={importMode === 'replace'}
                                    onChange={(e) => setImportMode(e.target.value)}
                                />
                                <strong>Replace All</strong> - Delete the entire catalog and import fresh
                            </label>
                        </div>

                        <div className="items-import-info">
                            <p><strong>CSV Format Required:</strong></p>
                            <p>Same columns as the Export CSV / original Product Attributes.csv (PM SKU, PM Grouped SKU, PM SAP SKU, PM Category, PM Subcategory, PM Series, PM Item Description, PM Grouped Name, PM SKU Type, PM Size, PM Primary Color, PM Is Open Box, PM Product Launch Date).</p>
                            <p>Every row needs a PM SKU.</p>
                        </div>

                        <div className="items-import-actions">
                            <input
                                type="file"
                                accept=".csv"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            <button onClick={() => fileInputRef.current?.click()} disabled={importing}>
                                {importing ? 'Importing...' : 'Select CSV File'}
                            </button>
                            <button
                                className="secondary"
                                onClick={() => setShowImportModal(false)}
                                disabled={importing}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .items-tab {
                    padding: 20px;
                }
                .items-tab-intro {
                    color: var(--text-secondary);
                    font-size: 13px;
                    margin-bottom: 16px;
                    max-width: 700px;
                }
                .items-form {
                    max-width: 700px;
                }
                .items-toolbar {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 16px;
                }
                .items-tab .form-group {
                    margin-bottom: 16px;
                }
                .items-tab .form-group label {
                    display: block;
                    margin-bottom: 4px;
                    font-weight: 600;
                    font-size: 14px;
                    color: var(--text-primary);
                }
                .items-tab .form-group input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid var(--border-input);
                    border-radius: 4px;
                    font-size: 14px;
                    background: var(--bg-input);
                    color: var(--text-primary);
                }
                .items-tab .form-row {
                    display: flex;
                    gap: 16px;
                }
                .items-tab .form-row .form-group {
                    flex: 1;
                }
                .items-tab .form-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 24px;
                }
                .items-tab .error {
                    background: rgba(234, 104, 82, 0.15);
                    color: var(--red);
                    padding: 10px 14px;
                    border-radius: 4px;
                    margin-bottom: 16px;
                    font-size: 14px;
                }
                .items-tab .success {
                    background: rgba(183, 205, 150, 0.2);
                    color: var(--text-primary);
                    padding: 10px 14px;
                    border-radius: 4px;
                    margin-bottom: 16px;
                    font-size: 14px;
                    border: 1px solid var(--green);
                }
                .items-import-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: var(--overlay-bg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10002;
                }
                .items-import-modal {
                    background: var(--bg-surface);
                    border-radius: 8px;
                    padding: 24px;
                    max-width: 500px;
                    width: 90%;
                    box-shadow: var(--shadow-lg);
                    color: var(--text-primary);
                }
                .items-import-modal h3 {
                    margin: 0 0 20px 0;
                    color: var(--text-primary);
                }
                .items-import-options {
                    margin-bottom: 20px;
                }
                .items-import-options label {
                    display: block;
                    padding: 12px;
                    border: 1px solid var(--border-input);
                    border-radius: 4px;
                    margin-bottom: 8px;
                    cursor: pointer;
                    color: var(--text-primary);
                }
                .items-import-options label:hover {
                    background: var(--bg-surface-tertiary);
                }
                .items-import-options input[type="radio"] {
                    margin-right: 8px;
                }
                .items-import-info {
                    background: var(--bg-surface-tertiary);
                    padding: 12px;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    font-size: 13px;
                    color: var(--text-secondary);
                }
                .items-import-info p {
                    margin: 4px 0;
                }
                .items-import-actions {
                    display: flex;
                    gap: 12px;
                    justify-content: flex-end;
                }
            `}</style>
        </div>
    );
}

function LogsTab() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventFilter, setEventFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const limit = 50;

    useEffect(() => {
        fetchLogs(true);
    }, [eventFilter]);

    async function fetchLogs(reset = false) {
        try {
            setLoading(true);
            const newOffset = reset ? 0 : offset;
            const params = new URLSearchParams();
            if (eventFilter) params.set('event', eventFilter);
            params.set('limit', limit);
            params.set('offset', newOffset);

            const res = await authFetch(`${API_BASE}/api/audit-logs?${params}`);
            if (!res.ok) throw new Error('Failed to fetch logs');
            const data = await res.json();

            if (reset) {
                setLogs(data);
                setOffset(data.length);
            } else {
                setLogs(prev => [...prev, ...data]);
                setOffset(newOffset + data.length);
            }
            setHasMore(data.length === limit);
        } catch (err) {
            console.error('Error fetching logs:', err);
        } finally {
            setLoading(false);
        }
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit'
        });
    };

    const eventTypes = [
        'USER_CREATED', 'USER_UPDATED', 'USER_DELETED', 'USER_LOGIN', 'USER_LOGIN_FAILED',
        'PROMOTION_CREATED', 'PROMOTION_UPDATED', 'PROMOTION_DELETED',
        'PROMOTIONS_IMPORTED', 'PROMOTIONS_EXPORTED',
        'AUTH_FAILED', 'ACCESS_DENIED', 'RATE_LIMITED', 'CSRF_FAILED'
    ];

    return (
        <div className="logs-tab">
            <div className="logs-filters">
                <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
                    <option value="">All Events</option>
                    {eventTypes.map(et => (
                        <option key={et} value={et}>{et.replace(/_/g, ' ')}</option>
                    ))}
                </select>
            </div>

            {loading && logs.length === 0 ? (
                <div className="loading">Loading logs...</div>
            ) : (
                <>
                    <div className="logs-table-container">
                        <table className="logs-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Event</th>
                                    <th>Severity</th>
                                    <th>Actor</th>
                                    <th>Target</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.id}>
                                        <td className="log-date">{formatDate(log.createdAt)}</td>
                                        <td>
                                            <span className="log-event-badge">
                                                {log.eventType.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`log-severity ${log.severity?.toLowerCase()}`}>
                                                {log.severity}
                                            </span>
                                        </td>
                                        <td>{log.actorEmail || '-'}</td>
                                        <td>{log.targetType ? `${log.targetType}:${log.targetId || ''}` : '-'}</td>
                                        <td className="log-details">
                                            {log.details ? (
                                                <span title={JSON.stringify(log.details, null, 2)}>
                                                    {Object.entries(log.details).map(([k, v]) =>
                                                        `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`
                                                    ).join(', ').substring(0, 100)}
                                                </span>
                                            ) : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {hasMore && (
                        <div className="logs-load-more">
                            <button className="secondary" onClick={() => fetchLogs(false)} disabled={loading}>
                                {loading ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}
                    {logs.length === 0 && !loading && (
                        <div className="empty-state">No audit logs found.</div>
                    )}
                </>
            )}
        </div>
    );
}

function FeedbackTab() {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [editingNotes, setEditingNotes] = useState({});

    useEffect(() => {
        fetchFeedback();
    }, []);

    async function fetchFeedback() {
        try {
            setLoading(true);
            const res = await authFetch(`${API_BASE}/api/feedback`);
            if (!res.ok) throw new Error('Failed to fetch feedback');
            setFeedback(await res.json());
        } catch (err) {
            console.error('Error fetching feedback:', err);
        } finally {
            setLoading(false);
        }
    }

    async function updateFeedback(id, updates) {
        try {
            const res = await authFetch(`${API_BASE}/api/feedback/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            if (!res.ok) throw new Error('Failed to update feedback');
            const updated = await res.json();
            setFeedback(prev => prev.map(f => f.id === id ? updated : f));
        } catch (err) {
            alert(err.message);
        }
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
        });
    };

    const filtered = feedback.filter(f => {
        if (typeFilter && f.type !== typeFilter) return false;
        if (statusFilter && f.status !== statusFilter) return false;
        if (priorityFilter && f.priority !== priorityFilter) return false;
        return true;
    });

    if (loading) return <div className="loading">Loading feedback...</div>;

    return (
        <div className="feedback-tab">
            <div className="feedback-filters">
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="">All Types</option>
                    <option value="bug">Bug Reports</option>
                    <option value="feature">Feature Requests</option>
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                    <option value="wont_do">Won't Do</option>
                </select>
                <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                </select>
            </div>

            {filtered.length === 0 ? (
                <div className="empty-state">No feedback found.</div>
            ) : (
                <div className="feedback-list">
                    {filtered.map(item => (
                        <div key={item.id} className={`feedback-card ${expandedId === item.id ? 'expanded' : ''}`}>
                            <div
                                className="feedback-card-header"
                                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                            >
                                <span className={`feedback-type-badge ${item.type}`}>
                                    {item.type === 'bug' ? 'Bug' : 'Feature'}
                                </span>
                                <span className="feedback-card-title">{item.title}</span>
                                <span className={`feedback-priority-badge ${item.priority}`}>
                                    {item.priority}
                                </span>
                                <select
                                    className={`feedback-status-select ${item.status}`}
                                    value={item.status}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateFeedback(item.id, { status: e.target.value })}
                                >
                                    <option value="open">Open</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                    <option value="closed">Closed</option>
                                    <option value="wont_do">Won't Do</option>
                                </select>
                                <span className="feedback-meta">
                                    {item.submittedByName || item.submittedByEmail} &middot; {formatDate(item.createdAt)}
                                </span>
                                <span className="feedback-expand-icon">{expandedId === item.id ? '\u25B2' : '\u25BC'}</span>
                            </div>

                            {expandedId === item.id && (
                                <div className="feedback-card-body">
                                    <div className="feedback-description">
                                        <strong>Description:</strong>
                                        <p>{item.description}</p>
                                    </div>
                                    <div className="feedback-admin-notes">
                                        <strong>Admin Notes:</strong>
                                        <textarea
                                            value={editingNotes[item.id] !== undefined ? editingNotes[item.id] : (item.adminNotes || '')}
                                            onChange={(e) => setEditingNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                                            placeholder="Add notes about this feedback..."
                                            rows={3}
                                        />
                                        {editingNotes[item.id] !== undefined && editingNotes[item.id] !== (item.adminNotes || '') && (
                                            <button
                                                className="save-notes-btn"
                                                onClick={() => {
                                                    updateFeedback(item.id, { admin_notes: editingNotes[item.id] });
                                                    setEditingNotes(prev => {
                                                        const next = { ...prev };
                                                        delete next[item.id];
                                                        return next;
                                                    });
                                                }}
                                            >
                                                Save Notes
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminPanel;
